import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Rocket, Target, Users, FileText, DollarSign, CheckCircle2,
  AlertTriangle, Clock, Zap, ChevronRight, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AutonomousBuild() {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [building, setBuilding] = useState(false);
  const [report, setReport] = useState(null);

  const executeBuildCommand = async () => {
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    setBuilding(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/autonomous/build`,
        { project_name: projectName },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setReport(response.data.execution_readiness_report);
      } else {
        toast.error(response.data.error || 'Build orchestration failed');
      }
    } catch (error) {
      console.error('Build command failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute build command');
    } finally {
      setBuilding(false);
    }
  };

  const approveEmails = async (buildSessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/autonomous/build/${buildSessionId}/approve-emails`,
        { confirmed: true },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );

      toast.success(response.data.message);
      
      // Reload report
      const reportResponse = await axios.get(
        `${API_URL}/api/autonomous/build/${buildSessionId}`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );
      setReport(reportResponse.data);
    } catch (error) {
      console.error('Email approval failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve emails');
    }
  };

  const approveFunding = async (buildSessionId, stage) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/autonomous/build/${buildSessionId}/approve-funding`,
        { confirmed: true, stage },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );

      toast.success(response.data.message);
      
      // Reload report
      const reportResponse = await axios.get(
        `${API_URL}/api/autonomous/build/${buildSessionId}`,
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );
      setReport(reportResponse.data);
    } catch (error) {
      console.error('Funding approval failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve funding');
    }
  };

  return (
    <AppShell>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="h-10 w-10 text-primary" />
            <div>
              <h1 className="text-4xl font-bold tracking-tight">
                Autonomous Founder Mode
              </h1>
              <p className="text-muted-foreground text-lg">
                LEE 7.0 • Single-command execution orchestration
              </p>
            </div>
          </div>

          <Alert className="border-primary/50 bg-primary/5">
            <Rocket className="h-4 w-4" />
            <AlertDescription>
              <strong>How it works:</strong> Type "Build [project_name]" and LEE autonomously orchestrates
              spec lock, vendor discovery, RFQ generation, and funding planning. All actions require your approval
              before execution.
            </AlertDescription>
          </Alert>
        </div>

        {/* Build Command Input */}
        {!report && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Execute Build Command</CardTitle>
              <CardDescription>
                Enter your project name to start autonomous execution orchestration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="project_name">Project Name</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        Build
                      </span>
                      <Input
                        id="project_name"
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="On Point Jar"
                        className="pl-16"
                        onKeyPress={(e) => e.key === 'Enter' && executeBuildCommand()}
                      />
                    </div>
                    <Button
                      onClick={executeBuildCommand}
                      disabled={building || !projectName.trim()}
                      className="gap-2 min-w-32"
                    >
                      {building ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                          Building...
                        </>
                      ) : (
                        <>
                          <Rocket className="h-4 w-4" />
                          Execute
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p className="font-semibold mb-2">What happens next:</p>
                  <ol className="space-y-1 ml-5 list-decimal">
                    <li>Spec Lock: Validate and freeze project definition</li>
                    <li>Vendor Discovery: Search and score manufacturers</li>
                    <li>RFQ Generation: Create professional request packets</li>
                    <li>Email Queue: Prepare vendor outreach (requires approval)</li>
                    <li>Funding Plan: Generate stage budgets (requires approval)</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Execution Readiness Report */}
        {report && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold">{report.project_name}</h2>
                <p className="text-muted-foreground">
                  Build Session: {report.build_session_id}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setReport(null)}>
                  New Build
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/execution')}
                  className="gap-2"
                >
                  <Target className="h-4 w-4" />
                  Go to Execution Hub
                </Button>
              </div>
            </div>

            {/* Status Overview */}
            <div className="grid grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">Spec Lock</p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">Vendor Discovery</p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">RFQ Generation</p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </CardContent>
              </Card>
              <Card className="border-yellow-500/50">
                <CardContent className="pt-6 text-center">
                  <Clock className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">Email Queue</p>
                  <p className="text-xs text-muted-foreground">Awaiting Approval</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">Funding Plan</p>
                  <p className="text-xs text-muted-foreground">Ready</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabbed Content */}
            <Tabs defaultValue="spec-lock" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="spec-lock">Spec Lock</TabsTrigger>
                <TabsTrigger value="vendors">Vendors</TabsTrigger>
                <TabsTrigger value="rfq">RFQ Packets</TabsTrigger>
                <TabsTrigger value="emails">Email Queue</TabsTrigger>
                <TabsTrigger value="funding">Funding Plan</TabsTrigger>
              </TabsList>

              {/* Spec Lock Tab */}
              <TabsContent value="spec-lock" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Specification Lock v{report.phase_1_spec_lock.version}</CardTitle>
                    <CardDescription>
                      Project definition frozen and validated
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Validation Status</h4>
                      <Badge variant={
                        report.phase_1_spec_lock.validation.overall_status === 'ready' 
                          ? 'default' 
                          : 'destructive'
                      }>
                        {report.phase_1_spec_lock.validation.overall_status}
                      </Badge>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Cost Estimate</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 border rounded">
                          <p className="text-sm text-muted-foreground">Best Case</p>
                          <p className="text-2xl font-bold">
                            ${report.phase_1_spec_lock.cost_estimate.total_estimate.best_case.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center p-3 border rounded border-primary">
                          <p className="text-sm text-muted-foreground">Likely</p>
                          <p className="text-2xl font-bold text-primary">
                            ${report.phase_1_spec_lock.cost_estimate.total_estimate.likely.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center p-3 border rounded">
                          <p className="text-sm text-muted-foreground">Worst Case</p>
                          <p className="text-2xl font-bold">
                            ${report.phase_1_spec_lock.cost_estimate.total_estimate.worst_case.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Engineering Brief</h4>
                      <div className="bg-muted p-4 rounded-lg font-mono text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                        {report.phase_1_spec_lock.engineering_brief}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Vendors Tab */}
              <TabsContent value="vendors" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Vendor Shortlist</CardTitle>
                    <CardDescription>
                      Top {report.phase_2_vendor_discovery.shortlist.length} vendors ranked by score
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {report.phase_2_vendor_discovery.shortlist.length === 0 ? (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          No vendors found in database. Add vendors to enable automatic discovery.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="space-y-3">
                        {report.phase_2_vendor_discovery.shortlist.map((vendor, index) => (
                          <div
                            key={vendor.vendor_id}
                            className="border rounded-lg p-4 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-4">
                              <div className="text-2xl font-bold text-muted-foreground">
                                #{index + 1}
                              </div>
                              <div>
                                <h4 className="font-semibold">{vendor.company_name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {vendor.region} • MOQ: {vendor.moq || 'N/A'} • 
                                  Lead time: {vendor.lead_time_days || 'N/A'} days
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline">
                                Score: {vendor.total_score}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* RFQ Tab */}
              <TabsContent value="rfq" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>RFQ Packets Generated</CardTitle>
                    <CardDescription>
                      {report.phase_3_rfq_packets.packets_generated} professional RFQ packets ready
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.phase_3_rfq_packets.rfq_packets.map((packet) => (
                        <div
                          key={packet.rfq_packet_id}
                          className="border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{packet.vendor_name}</h4>
                            <Button variant="outline" size="sm" className="gap-2">
                              <Download className="h-3 w-3" />
                              Download
                            </Button>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <p>Includes: Engineering Brief, NDA, BOM</p>
                            <p>Deadline: {packet.deadline_days} days</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Email Queue Tab */}
              <TabsContent value="emails" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Approval Queue</CardTitle>
                    <CardDescription>
                      {report.phase_4_email_queue.emails_queued} emails queued for your approval
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {report.phase_4_email_queue.status === 'awaiting_approval' && (
                      <Alert className="border-yellow-500/50 bg-yellow-500/10">
                        <Clock className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Approval Required:</strong> Review vendor emails below and approve to proceed.
                          No emails will be sent without your explicit approval.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-3">
                      {report.phase_4_email_queue.queue.slice(0, 3).map((email) => (
                        <div
                          key={email.email_id}
                          className="border rounded-lg p-4"
                        >
                          <div className="mb-2">
                            <p className="font-semibold">{email.subject}</p>
                            <p className="text-sm text-muted-foreground">To: {email.to}</p>
                          </div>
                          <div className="bg-muted p-3 rounded text-sm whitespace-pre-wrap max-h-32 overflow-y-auto mb-3">
                            {email.body.substring(0, 300)}...
                          </div>
                          <Badge variant="outline">{email.status}</Badge>
                        </div>
                      ))}
                    </div>

                    {report.phase_4_email_queue.status === 'awaiting_approval' && (
                      <Button
                        onClick={() => approveEmails(report.build_session_id)}
                        className="w-full gap-2"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Approve All Emails ({report.phase_4_email_queue.emails_queued})
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Funding Plan Tab */}
              <TabsContent value="funding" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Milestone Funding Plan</CardTitle>
                    <CardDescription>
                      Stage-by-stage budget with approval gates
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Total Budget</p>
                        <p className="text-2xl font-bold">
                          ${report.phase_5_funding_plan.plan.total_budget.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Contingency (30%)</p>
                        <p className="text-2xl font-bold">
                          ${report.phase_5_funding_plan.plan.contingency_buffer.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground">Worst Case</p>
                        <p className="text-2xl font-bold text-orange-600">
                          ${report.phase_5_funding_plan.plan.worst_case_scenario.toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {report.phase_5_funding_plan.plan.stages.map((stage) => (
                        <div
                          key={stage.stage}
                          className="border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-semibold">
                                Stage {stage.stage}: {stage.name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                Budget: ${stage.budget.toLocaleString()}
                              </p>
                            </div>
                            <Badge variant={
                              stage.status === 'approved' ? 'default' :
                              stage.status === 'pending_approval' ? 'outline' :
                              'secondary'
                            }>
                              {stage.status.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <ul className="text-sm space-y-1">
                            {stage.activities.map((activity, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <ChevronRight className="h-3 w-3" />
                                {activity}
                              </li>
                            ))}
                          </ul>
                          {stage.status === 'pending_approval' && (
                            <Button
                              size="sm"
                              onClick={() => approveFunding(report.build_session_id, stage.stage)}
                              className="mt-3 gap-2"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Approve Stage {stage.stage}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>

                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Important:</strong> Approving stages does not authorize spending.
                        Use Capital Authorization for actual fund disbursement.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </AppShell>
  );
}
